import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useToast } from "@/components/Toast";
import { Badge, Button, Card, Empty, ErrorNote, inputClass } from "@/components/ui";
import { api, type SuggestionResponse } from "@/lib/api";
import { formatDateTime, formatMoneyCents } from "@/lib/format";
import { type LeadStage } from "@/lib/api";
import { STAGE_LABEL } from "@/lib/stages";

/**
 * §8.1's suggestion, and the two buttons that are the whole point of it.
 *
 * Rule 0 (§1): agents draft, score and recommend; Sam clicks. So this card is read-only until a
 * button is pressed, and it shows the reasoning next to the recommendation rather than below the
 * fold — a suggestion you have to expand to evaluate gets accepted without being evaluated.
 *
 * The provenance line is not debug output. Reviewing a run of bad suggestions without knowing they
 * all came from one prompt version makes a systematic problem look like a series of unlucky calls.
 */

const SIGNAL_LABEL: Record<string, string> = {
  tax_delinquent: "Tax delinquent",
  estate_heirs: "Estate / heirs",
  absentee: "Absentee owner",
  price_given: "Price given",
  timeline_stated: "Timeline stated",
  vacant_land: "Vacant land",
  code_violation: "Code violation",
  divorce: "Divorce",
  relocation: "Relocation",
  financial_distress: "Financial distress",
  listed_previously: "Listed previously",
  multiple_parcels: "Multiple parcels",
};

type Triage = {
  reply_class?: string;
  motivation_signals?: string[];
  asking_price_cents?: number | null;
  timeline?: string | null;
  heirs_estate?: boolean;
  recommended_stage?: string;
  recommended_next_action?: { text?: string; due_offset_days?: number };
  confidence?: number;
  rationale?: string;
};

/** A held before/after value: a next action's text or a score. */
function heldText(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "—";
}

/** The suggestion's stage is a plain string off the wire; STAGE_LABEL is keyed by the enum. */
function stageLabel(value: string): string {
  return STAGE_LABEL[value as LeadStage] ?? value;
}

function statusTone(status: string | null): "ok" | "warn" | "muted" | "info" {
  if (status === "accepted") return "ok";
  if (status === "rejected") return "muted";
  if (status === "pending") return "info";
  return "muted";
}

export function AgentSuggestion({ leadId, stage }: { leadId: string; stage: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [showReason, setShowReason] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["suggestion", leadId],
    queryFn: () => api.suggestion(leadId),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["suggestion", leadId] });
    void qc.invalidateQueries({ queryKey: ["lead", leadId] });
    void qc.invalidateQueries({ queryKey: ["overview"] });
    void qc.invalidateQueries({ queryKey: ["nav-counts"] });
  };

  const accept = useMutation({
    mutationFn: () => api.acceptSuggestion(leadId),
    onSuccess: () => {
      toast.push("Suggestion applied");
      invalidate();
    },
    onError: (e: Error) => {
      toast.push(e.message, "danger");
    },
  });

  const reject = useMutation({
    mutationFn: () => api.rejectSuggestion(leadId, reason.trim() || undefined),
    onSuccess: () => {
      toast.push("Suggestion rejected");
      setShowReason(false);
      setReason("");
      invalidate();
    },
    onError: (e: Error) => {
      toast.push(e.message, "danger");
    },
  });

  const triage = useMutation({
    mutationFn: () => api.triageLead(leadId),
    onSuccess: (run) => {
      if (run.status === "ok") toast.push("Triage complete");
      else if (run.status === "refused_budget") toast.push("Refused: the monthly agent budget is spent", "danger");
      else toast.push(run.error ?? "Triage failed", "danger");
      invalidate();
    },
    onError: (e: Error) => {
      toast.push(e.message, "danger");
    },
  });

  if (isLoading) return <Card title="Agent suggestion">Loading…</Card>;
  if (error)
    return (
      <Card title="Agent suggestion">
        <ErrorNote error={error} />
      </Card>
    );

  const s: SuggestionResponse | undefined = data;
  const body = (s?.suggestion ?? null) as Triage | null;
  const pending = s?.status === "pending";

  const runNow = (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => triage.mutate()}
      disabled={triage.isPending}
      data-testid="suggestion-run"
    >
      {triage.isPending ? "Triaging…" : s?.status ? "Re-triage" : "Triage now"}
    </Button>
  );

  if (!body) {
    return (
      <Card title="Agent suggestion" action={runNow} data-testid="agent-suggestion">
        <Empty
          title="None pending"
          hint="§8.1 triage runs automatically on leads that arrive with messages or notes. Nothing is ever applied without a click."
        />
      </Card>
    );
  }

  const confidence = typeof body.confidence === "number" ? Math.round(body.confidence * 100) : null;
  const action = body.recommended_next_action;
  const stageChanges = body.recommended_stage && body.recommended_stage !== stage;

  return (
    <Card title="Agent suggestion" action={runNow} data-testid="agent-suggestion">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={statusTone(s?.status ?? null)} data-testid="suggestion-status">
          {s?.status ?? "none"}
        </Badge>
        {body.reply_class && (
          <Badge tone="muted" data-testid="suggestion-reply-class">
            {body.reply_class.replace(/_/g, " ")}
          </Badge>
        )}
        {confidence !== null && (
          <span className="text-[11px] tabular-nums text-fg-muted" data-testid="suggestion-confidence">
            {confidence}% confident
          </span>
        )}
      </div>

      {s?.status === "superseded" && (
        <p className="mt-2 text-xs text-fg-muted" data-testid="suggestion-superseded">
          Nothing left to decide: its only change was a stage this lead is already at, once the
          v1 stage names are read in v2 terms. Resolved as superseded.
        </p>
      )}
      {pending && s?.held && Object.keys(s.held).length > 0 && (
        <div
          className="mt-2 space-y-1 rounded border border-warn/40 bg-warn/10 px-2 py-1.5 text-xs"
          data-testid="suggestion-held"
        >
          <p className="font-medium">Held for you — not applied automatically</p>
          {Object.entries(s.held).map(([cls, raw]) => {
            const entry = (raw ?? {}) as { reason?: string; before?: unknown; after?: unknown };
            const showDiff = entry.before !== undefined || entry.after !== undefined;
            return (
              <div key={cls} data-testid={`suggestion-held-${cls}`}>
                <span className="text-fg-muted">{cls.replace(/_/g, " ")}:</span>{" "}
                {showDiff && (
                  <>
                    <span className="line-through" data-testid={`suggestion-held-before-${cls}`}>
                      {heldText(entry.before)}
                    </span>{" "}
                    →{" "}
                    <strong data-testid={`suggestion-held-after-${cls}`}>
                      {heldText(entry.after)}
                    </strong>
                  </>
                )}
                {entry.reason && <span className="block text-[11px] text-fg-muted">{entry.reason}</span>}
              </div>
            );
          })}
        </div>
      )}

      <dl className="mt-2 space-y-1.5 text-xs">
        {body.recommended_stage && (
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-fg-muted">Stage</dt>
            <dd data-testid="suggestion-stage">
              {stageChanges ? (
                <>
                  {stageLabel(stage)} →{" "}
                  <strong>{stageLabel(body.recommended_stage)}</strong>
                </>
              ) : (
                <>stay at {stageLabel(stage)}</>
              )}
            </dd>
          </div>
        )}
        {action?.text && (
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-fg-muted">Next action</dt>
            <dd data-testid="suggestion-next-action">
              {action.text}
              {typeof action.due_offset_days === "number" && (
                <span className="text-fg-muted">
                  {" "}
                  · in {action.due_offset_days} day{action.due_offset_days === 1 ? "" : "s"}
                </span>
              )}
            </dd>
          </div>
        )}
        {typeof body.asking_price_cents === "number" && (
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-fg-muted">Asking</dt>
            <dd className="tabular-nums" data-testid="suggestion-asking">
              {formatMoneyCents(body.asking_price_cents)}
            </dd>
          </div>
        )}
        {body.timeline && (
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-fg-muted">Timeline</dt>
            <dd>{body.timeline}</dd>
          </div>
        )}
        {body.motivation_signals && body.motivation_signals.length > 0 && (
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-fg-muted">Signals</dt>
            <dd className="flex flex-wrap gap-1" data-testid="suggestion-signals">
              {body.motivation_signals.map((sig) => (
                <Badge key={sig} tone="muted">
                  {SIGNAL_LABEL[sig] ?? sig}
                </Badge>
              ))}
            </dd>
          </div>
        )}
      </dl>

      {body.rationale && (
        <p className="mt-2 rounded border-l-2 pl-2 text-xs text-fg-muted" data-testid="suggestion-rationale">
          {body.rationale}
        </p>
      )}

      {pending && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() => accept.mutate()}
            disabled={accept.isPending}
            data-testid="suggestion-accept"
          >
            {accept.isPending ? "Applying…" : "Accept"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowReason((v) => !v)}
            data-testid="suggestion-reject-open"
          >
            Reject
          </Button>
        </div>
      )}

      {pending && showReason && (
        <div className="mt-2 space-y-1.5">
          <input
            className={inputClass + " w-full"}
            placeholder="Why? (optional, but it is how the prompt gets fixed)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Rejection reason"
            data-testid="suggestion-reject-reason"
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => reject.mutate()}
            disabled={reject.isPending}
            data-testid="suggestion-reject-confirm"
          >
            {reject.isPending ? "Rejecting…" : "Confirm reject"}
          </Button>
        </div>
      )}

      <p className="mt-2 text-[11px] text-fg-muted" data-testid="suggestion-provenance">
        {s?.model ?? "unknown model"} · {s?.prompt_hash ?? "no prompt hash"}
        {s?.created_at ? ` · ${formatDateTime(s.created_at)}` : ""}
      </p>
    </Card>
  );
}
